import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from '../hooks/use-toast';
import { ShieldPlus, Upload, FileSpreadsheet, FileText, Trash2, Eye, Calendar, Plus, Save } from 'lucide-react';
import InsuranceCtcSummary from '../components/insurance/InsuranceCtcSummary';
import BulkInsuranceUpload from '../components/insurance/BulkInsuranceUpload';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDateTime = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return d; }
};

const fileIcon = (ext) => {
  if ((ext || '').toLowerCase() === '.pdf') return <FileText className="h-6 w-6 text-rose-500" />;
  return <FileSpreadsheet className="h-6 w-6 text-emerald-600" />;
};

const InsuranceCoverageAdmin = () => {
  const [files, setFiles] = useState([]);
  const [title, setTitle] = useState('Insurance & Hospital Coverage');
  const [titleSaving, setTitleSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pendingTitle, setPendingTitle] = useState('');
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/insurance/global`);
      setFiles(r.data?.files || []);
      setTitle(r.data?.title || 'Insurance & Hospital Coverage');
    } catch { setFiles([]); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const saveTitle = async () => {
    setTitleSaving(true);
    try {
      const fd = new FormData();
      fd.append('title', title);
      await axios.put(`${API}/api/insurance/global/title`, fd);
      toast({ title: 'Section title updated' });
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setTitleSaving(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (pendingTitle) fd.append('file_title', pendingTitle);
      await axios.post(`${API}/api/insurance/global`, fd);
      toast({ title: 'File added', description: 'Visible to all employees on the portal.' });
      setPendingTitle('');
      if (fileRef.current) fileRef.current.value = '';
      await load();
    } catch (e) {
      toast({ title: 'Upload failed', description: e?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this file? Employees will no longer see it.')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/api/insurance/global/${id}`);
      await load();
    } catch (e) {
      toast({ title: 'Delete failed', description: e?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4 max-w-4xl" data-testid="insurance-coverage-admin">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <ShieldPlus className="h-7 w-7 text-emerald-600" /> Insurance
        </h1>
        <p className="text-gray-600 mt-1">
          Track total insurance cost-to-company and share coverage documents with staff.
        </p>
      </div>

      {/* Insurance CTC summary (staff + dependents + visa holders, minus refunds) */}
      <InsuranceCtcSummary />

      {/* Bulk upload insurance cards (ZIP, matched by first name) */}
      <BulkInsuranceUpload />

      {/* Section title */}
      <Card>
        <CardContent className="p-5 space-y-3">
          <div>
            <Label className="text-xs">Section Title</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Insurance & Hospital Coverage 2026"
                data-testid="coverage-section-title"
              />
              <Button type="button" onClick={saveTitle} disabled={titleSaving} variant="outline" data-testid="coverage-section-title-save">
                <Save className="h-4 w-4 mr-1" /> {titleSaving ? 'Saving…' : 'Save'}
              </Button>
            </div>
            <p className="text-[10px] text-gray-400 mt-1">Shown as the heading on the employee portal.</p>
          </div>
        </CardContent>
      </Card>

      {/* Add new file */}
      <Card>
        <CardContent className="p-5 space-y-3">
          <p className="text-sm font-semibold flex items-center gap-2">
            <Plus className="h-4 w-4" /> Add a file
          </p>
          <div>
            <Label className="text-xs">Display name (optional)</Label>
            <Input
              value={pendingTitle}
              onChange={e => setPendingTitle(e.target.value)}
              placeholder="e.g. Hospital Network List 2026"
              data-testid="coverage-pending-title"
            />
            <p className="text-[10px] text-gray-400 mt-1">Defaults to the original filename.</p>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv,.pdf"
            className="hidden"
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid="coverage-file-input"
          />
          <Button onClick={() => fileRef.current?.click()} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="coverage-add-file">
            <Upload className="h-4 w-4 mr-1" /> {busy ? 'Uploading…' : 'Upload File'}
          </Button>
          <p className="text-[10px] text-gray-400">Allowed: .xlsx, .xls, .csv, .pdf</p>
        </CardContent>
      </Card>

      {/* List of files */}
      <Card>
        <CardContent className="p-5 space-y-3">
          <p className="text-sm font-semibold">
            Uploaded files <span className="text-gray-400 font-normal">({files.length})</span>
          </p>
          {loading ? (
            <div className="py-8 text-center text-gray-500 text-sm">Loading…</div>
          ) : files.length === 0 ? (
            <div className="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
              <FileSpreadsheet className="h-10 w-10 mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-600">No files uploaded yet.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {files.map(rec => (
                <div key={rec.id} className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-4" data-testid={`coverage-file-${rec.id}`}>
                  <div className="flex items-start gap-3">
                    {fileIcon(rec.ext)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate" data-testid={`coverage-title-${rec.id}`}>
                        {rec.title || rec.filename}
                      </p>
                      <p className="text-[11px] text-gray-500 mt-0.5 truncate" title={rec.filename}>{rec.filename}</p>
                      <p className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-1">
                        <Calendar className="h-3 w-3" /> Uploaded {formatDateTime(rec.uploaded_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-emerald-200">
                    <a
                      href={`${API}${rec.url}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-xs bg-white border hover:bg-gray-50"
                      data-testid={`coverage-view-${rec.id}`}
                    >
                      <Eye className="h-3 w-3" /> View / Download
                    </a>
                    <Button size="sm" variant="ghost" onClick={() => remove(rec.id)} disabled={busy} className="text-rose-600" data-testid={`coverage-delete-${rec.id}`}>
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InsuranceCoverageAdmin;
